import { useCallback, useState, useRef, CSSProperties } from 'react';
import {
  Wrapper,
  Hero as StyledHero,
  HeroForegroundBackground,
  HeroForeground,
} from './hero.style';
import { Props } from './hero-preview.constants';
import { hexColourToRGBArray } from 'lib/strings/strings.colour';
import { ActionButtonWrapper } from 'approot/shared/action-button/action-button';
import { coolGrey1 } from 'approot/shared/styles/colours.style';
import { useResponsiveImage } from 'approot/shared/images/images.hooks';

const HeroPreview = ({
  rounded,
  actionButtons,
  fadeToColour,
  images,
  onImageLoaded,
  isEditing,
  url,
}: Props) => {
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const containerRef = useRef<HTMLElement | null>(null);

  const responsiveImageUrl = useResponsiveImage(images || [], 900);

  const imageUrl = url || responsiveImageUrl;

  const cssImageUrl = imageUrl && `url("${imageUrl}")`;

  const rgbColour = hexColourToRGBArray(fadeToColour);
  const rgbCommaSeparated = rgbColour.join(',');
  const backgroundImage = isEditing ? '' : cssImageUrl;
  const backgroundStyle = {
    backgroundImage: backgroundImage,
  };
  const backgroundColourStyle = {
    backgroundColor: fadeToColour ? `rgb(${rgbCommaSeparated})` : coolGrey1,
  };
  const gradientHeight =
    containerRef && containerRef.current
      ? containerRef.current.children.length > 0
        ? containerRef.current.children[0].clientHeight
        : containerRef.current.clientHeight
      : 0;

  const containerStyle = isEditing
    ? ({
        marginTop: -gradientHeight,
        pointerEvents: 'none',
        visibility: !imageLoaded ? 'hidden' : 'inherit',
      } as CSSProperties)
    : undefined;

  const imageHasLoaded = useCallback(() => {
    setImageLoaded(true);

    onImageLoaded &&
      containerRef.current &&
      onImageLoaded(
        containerRef.current.children[0].clientWidth,
        containerRef.current.children[0].clientHeight
      );
  }, [setImageLoaded, onImageLoaded]);

  return (
    <Wrapper
      aria-label="Preview of hero banner"
      ref={(ref: null | HTMLElement) => {
        containerRef.current = ref;
      }}
      style={containerStyle}
    >
      {actionButtons && <ActionButtonWrapper buttons={actionButtons} />}
      <StyledHero
        $isPreview
        $isEditing={isEditing}
        data-selector="hero-image"
        style={isEditing ? undefined : backgroundColourStyle}
        $rounded={rounded}
      >
        <HeroForegroundBackground
          $isEditing={isEditing}
          $fadeInHero={imageLoaded}
          style={backgroundStyle}
          $rounded={rounded}
        />
        <HeroForeground $isPreview $isEditing={isEditing} />
      </StyledHero>
      {imageUrl && (
        <div
          style={{
            width: 0,
            height: 0,
            overflow: 'hidden',
            position: 'absolute',
          }}
        >
          <img src={imageUrl} onLoad={imageHasLoaded} alt="" />
        </div>
      )}
    </Wrapper>
  );
};

export default HeroPreview;
